import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Grid, IconButton, Tooltip, Button, CircularProgress, Snackbar, Alert, Avatar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import axios from '../axios';
import Layout from './Layout';
import CourseMTDetailModal from './CourseMTDetailModal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import Valutazioni from './Valutazioni';
import AlunnoModal from './AlunnoModal';

const MieiDinoAlunni = () => {
    const [alunni, setAlunni] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [selectedAlunnoId, setSelectedAlunnoId] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [currentAlunno, setCurrentAlunno] = useState({
        first_name: '',
        last_name: '',
        email: '',
    });

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchAlunni = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/datahandler/all-alunni-insegnante', { token });
            setAlunni(response.data);
        } catch (error) {
            console.error('Error fetching alunni:', error);
            setSnackbarMessage('Errore durante il caricamento degli alunni.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAlunni();
    }, []);

    const handleCourseClick = async (courseId) => {
        setSelectedCourseId(courseId);
    };

    const handleValutazioniClick = (alunnoId) => {
        setSelectedAlunnoId(alunnoId === selectedAlunnoId ? null : alunnoId);
    };

    const handleCreateAlunno = () => {
        setCurrentAlunno({
            first_name: '',
            last_name: '',
            email: '',
        });
        setOpenModal(true);
    };

    const handleEditAlunno = (alunno) => {
        setCurrentAlunno(alunno);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleSaveAlunno = async () => {
        const token = localStorage.getItem('token');
        const url = currentAlunno.id ? '/datahandler/update-alunno' : '/datahandler/create-alunno';

        try {
            await axios.post(url, { ...currentAlunno, token });
            fetchAlunni();
            handleCloseModal();
            setSnackbarMessage('Alunno salvato con successo.');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Error saving alunno:', error);
            if (error.response && error.response.data && error.response.data.error) {
                setSnackbarMessage(error.response.data.error);
            } else {
                setSnackbarMessage('Errore durante il salvataggio dell\'alunno.');
            }
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setOpenSnackbar(false);
    };

    const renderCard = (alunno) => {
        const hasCourse = Boolean(alunno.course_name);
        return (
            <Card
                key={alunno.id}
                className="glass-card"
                elevation={0}
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    border: hasCourse ? '1px solid rgba(76, 175, 80, 0.3) !important' : ''
                }}
            >
                <Box sx={{ position: 'relative', pt: 4, pb: 2, px: 2, textAlign: 'center' }}>
                    <Tooltip title="Modifica Alunno">
                        <IconButton
                            sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.8)' }
                            }}
                            size="small"
                            onClick={() => handleEditAlunno(alunno)}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Avatar
                        sx={{
                            width: 64,
                            height: 64,
                            mx: 'auto',
                            mb: 2,
                            background: hasCourse ? 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)' : 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                        }}
                    >
                        <PersonIcon />
                    </Avatar>

                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', mb: 0.5 }}>
                        {alunno.first_name} {alunno.last_name}
                    </Typography>

                    <Typography variant="body2" sx={{ color: '#666', mb: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {alunno.uemail || 'Nessun account'}
                    </Typography>
                </Box>

                <CardContent sx={{ flexGrow: 1, pt: 0, px: 2, pb: '16px !important', textAlign: 'center' }}>
                    <Box sx={{ minHeight: 80, mb: 2 }}>
                        {alunno.email && (
                            <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                                Email comunicazioni: {alunno.email}
                            </Typography>
                        )}

                        {alunno.course_name ? (
                            <Button
                                variant="outlined"
                                color="success"
                                size="small"
                                onClick={() => handleCourseClick(alunno.course_id)}
                                sx={{
                                    borderRadius: '20px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderWidth: '1px',
                                    backgroundColor: 'rgba(76, 175, 80, 0.05)',
                                    '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)', borderWidth: '1px' }
                                }}
                                startIcon={<CheckCircleIcon sx={{ fontSize: '16px !important' }} />}
                            >
                                {alunno.course_name}
                            </Button>
                        ) : (
                            <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic', mt: 1 }}>
                                Non associato a corsi
                            </Typography>
                        )}
                    </Box>

                    {alunno.hasValutazioni === '1' && (
                        <Button
                            variant="contained"
                            fullWidth
                            color="inherit"
                            onClick={() => handleValutazioniClick(alunno.id)}
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 700,
                                py: 1,
                                backgroundColor: '#f8f9fa',
                                color: '#333',
                                boxShadow: 'none',
                                border: '1px solid rgba(0,0,0,0.05)',
                                '&:hover': { backgroundColor: '#e9ecef', boxShadow: 'none' }
                            }}
                        >
                            {selectedAlunnoId === alunno.id ? 'Nascondi Valutazioni' : 'Visualizza Valutazioni'}
                        </Button>
                    )}

                    {selectedAlunnoId === alunno.id && (
                        <Box sx={{ mt: 2, textAlign: 'left' }}>
                            <Valutazioni idAlunno={alunno.id} />
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    return (
        <Layout>
            <Box className="dashboard-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography className="dashboard-title">
                        I Miei DinoAlunni
                    </Typography>
                    <Typography variant="body1" className="dashboard-subtitle">
                        Gestisci i tuoi studenti, visualizza le valutazioni e associa i corsi.
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleCreateAlunno}
                    sx={{
                        borderRadius: '12px',
                        py: 1.2,
                        px: 3,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '1rem',
                        boxShadow: '0 8px 16px rgba(255, 152, 0, 0.3)',
                        '&:hover': { boxShadow: '0 12px 20px rgba(255, 152, 0, 0.4)' }
                    }}
                >
                    Crea Nuovo Alunno
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress sx={{ color: '#ff9800' }} />
                </Box>
            ) : (
                <Grid container spacing={isMobile ? 2 : 4} sx={{ width: '100%', m: 0 }}>
                    {alunni.length > 0 ? (
                        alunni.map((alunno) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={alunno.id}>
                                {renderCard(alunno)}
                            </Grid>
                        ))
                    ) : (
                        <Grid item xs={12}>
                            <Box className="glass-card" sx={{ p: 8, textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ color: '#777' }}>
                                    Non ci sono ancora DinoAlunni associati a te.
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>
                                    Inizia creando un nuovo alunno o assegnandone uno ai tuoi corsi.
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            )}

            <AlunnoModal
                open={openModal}
                onClose={handleCloseModal}
                onSave={handleSaveAlunno}
                alunno={currentAlunno}
                setAlunno={setCurrentAlunno}
            />

            {selectedCourseId && (
                <CourseMTDetailModal
                    open={Boolean(selectedCourseId)}
                    onClose={() => setSelectedCourseId(null)}
                    idCorso={selectedCourseId}
                    refreshCourses={fetchAlunni}
                />
            )}

            <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: '12px' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Layout>
    );
};

export default MieiDinoAlunni;
